import { MagnifyingGlassIcon } from '@heroicons/react/16/solid'
import { useEffect, useState } from 'react'
import { Badge } from '../catalyst/badge'
import { Button } from '../catalyst/button'
import { Heading } from '../catalyst/heading'
import { Input, InputGroup } from '../catalyst/input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../catalyst/table'
import { Text } from '../catalyst/text'
import { CATEGORY_LABELS, useFertilizers } from '../lib/fertilizers'
import type { FertilizerProduct } from '../lib/types'
import { Shell } from './shell'

const DEBOUNCE_MS = 300

export function OrganicBadge({ product }: { product: FertilizerProduct }) {
  switch (product.organicStatus) {
    case 'ORGANIC':
      return <Badge color="green">Organic</Badge>
    case 'ORGANIC_BASED':
      return <Badge color="lime">Organic-based</Badge>
    case 'CONVENTIONAL':
      return <Badge color="zinc">Conventional</Badge>
    default:
      return <span>—</span>
  }
}

export function FertilizersPage() {
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [query])

  const { items, loading, loadingMore, error, hasMore, loadMore } = useFertilizers(debouncedQuery)

  return (
    <Shell>
      <div className="flex items-center justify-between gap-4">
        <div>
          <Heading>Fertilizers</Heading>
          <Text>
            Plant-nutrition products from BC and Canadian suppliers. Compiled from the suppliers' websites, so check the product label before
            you buy or apply.
          </Text>
        </div>
      </div>

      <div className="mt-6 max-w-md">
        <InputGroup>
          <MagnifyingGlassIcon data-slot="icon" />
          <Input
            type="search"
            placeholder="Search by name, brand or supplier…"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            aria-label="Search fertilizers"
          />
        </InputGroup>
      </div>

      <div className="mt-8">
        {error ? (
          <div className="rounded-lg border border-dashed border-red-300 p-10 text-center dark:border-red-900">
            <Text>{error}</Text>
          </div>
        ) : loading ? (
          <div className="rounded-lg border border-dashed border-zinc-950/15 p-10 text-center dark:border-white/15">
            <Text>Loading fertilizers…</Text>
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-lg border border-dashed border-zinc-950/15 p-10 text-center dark:border-white/15">
            <Text>{debouncedQuery ? `No products match "${debouncedQuery}".` : 'No products found.'}</Text>
          </div>
        ) : (
          <>
            <Table striped>
              <TableHead>
                <TableRow>
                  <TableHeader>Name</TableHeader>
                  <TableHeader>Supplier</TableHeader>
                  <TableHeader>Analysis</TableHeader>
                  <TableHeader>Type</TableHeader>
                  <TableHeader>Organic</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((product) => (
                  <TableRow key={product.id} href={`/fertilizers/${product.id}`}>
                    <TableCell className="font-medium text-zinc-950 dark:text-white">{product.name}</TableCell>
                    <TableCell>
                      {product.supplier}
                      {product.brand && product.brand !== product.supplier && <span className="block text-zinc-500 dark:text-zinc-400">{product.brand}</span>}
                    </TableCell>
                    <TableCell>{product.analysisRaw || '—'}</TableCell>
                    <TableCell>{CATEGORY_LABELS[product.category]}</TableCell>
                    <TableCell>
                      <OrganicBadge product={product} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {hasMore && !debouncedQuery && (
              <div className="mt-4 flex justify-center">
                <Button outline disabled={loadingMore} onClick={loadMore}>
                  {loadingMore ? 'Loading…' : 'Load more'}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </Shell>
  )
}
